import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';

import { environment } from 'environments/environment';

/** Dónde se recuerda el agente elegido entre visitas */
const STORAGE_KEY = 'bryant-agent';

/**
 * Un despliegue del agente Bryant: a dónde se le habla y con qué app
 * registration se pide el token.
 */
export interface ChatAgentOption {
  /** Lo que se guarda en el storage y en cada hilo del historial */
  key: string;
  /** Lo que se lee en el selector: "DEV", "QA" */
  label: string;
  url: string;
  id: string;
}

/**
 * Contra qué despliegue de Bryant habla el chat. Los despliegues los declara el
 * environment en `agents.bryantOptions`; si no hay más de uno no se dibuja el
 * selector y se usa `agents.bryant`, como siempre.
 */
@Injectable({ providedIn: 'root' })
export class ChatAgentService {

  /** Los despliegues entre los que se puede elegir. Vacío en producción */
  public readonly options: ChatAgentOption[] = environment.agents.bryantOptions ?? [];

  /** El que trae el environment, se usa cuando no hay nada que elegir */
  private readonly fallback: ChatAgentOption = { key: 'default', label: '', ...environment.agents.bryant };

  private readonly _current = new BehaviorSubject<ChatAgentOption>(this.initial());

  /** El agente activo */
  get current(): ChatAgentOption { return this._current.value; }

  /** Emite en cada cambio de agente */
  get current$(): Observable<ChatAgentOption> { return this._current.asObservable(); }

  /** Base de la api del agente activo */
  get url(): string { return this.current.url; }

  /** Hay algo que elegir: con uno solo no se dibuja el selector */
  get hasChoice(): boolean { return this.options.length > 1; }

  /** No es el que el environment usa por omisión: la vista lo marca */
  get isSecondary(): boolean {
    return this.hasChoice && this.current.url !== environment.agents.bryant.url;
  }

  /**
   * Cambia de agente.
   * @param {string} key Llave del despliegue
   * @returns {boolean} Devuelve si hubo cambio
   */
  select(key: string): boolean {
    if (!key || key === this.current.key) return false;

    const option = this.options.find(item => item.key === key);
    if (!option) return false;

    this.store(option.key);
    this._current.next(option);
    return true;
  }

  // ─────────────────────────────────────────────────────────────────────────

  /** El guardado si sigue existiendo, si no el que coincide con `bryant`, si no el primero */
  private initial(): ChatAgentOption {
    if (this.options.length === 0) return this.fallback;

    const stored = this.readStored();

    return this.options.find(item => item.key === stored)
      ?? this.options.find(item => item.url === environment.agents.bryant.url)
      ?? this.options[0];
  }

  // El almacenamiento puede estar bloqueado (modo privado, políticas del
  // equipo); si falla solo se pierde recordar la elección
  private readStored(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private store(key: string): void {
    try {
      localStorage.setItem(STORAGE_KEY, key);
    } catch {
      // sin persistencia, pero la sesión actual funciona igual
    }
  }
}
